import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  message,
  Select,
  Space,
  Table,
  TableColumnsType,
  Tabs,
  Tooltip,
  Upload,
} from 'antd';
import * as Component from 'components';
import { EnhancedCastEditor } from 'components/enhanced-cast';
import CustomSelector, { CustomSelectorTabInterface } from 'components/selector/Selector';
import {
  TransferDataHandlerInterface,
  TransferDataSourceInterface,
} from 'components/transfer/Transfer';
import { AGE_RATING, THUMBNAIL_UPLOAD_STATE } from 'constants/index';
import { globalSettingService } from 'content/global-settings/services/globalSettingService';
import {
  CountryInterface,
  LanguageInterface,
} from 'content/global-settings/types/globalSettingType';
import { seasonService } from 'content/service/seasonService';
import { seriesSeasonItemService } from 'content/service/seriesSeasonService';
import { seriesService } from 'content/service/seriesService';
import { ElevenDbSeriesInterface, SeriesApiRequest, SeriesData } from 'content/type/seriesType';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { genreApiService } from 'service/api/genreApiService';
import { peopleApiService } from 'service/api/peopleApiService';
import { tagApiService } from 'service/api/tagApiService';
import { GenreInterface } from 'type/genreType';
import { PeopleInterface } from 'type/peopleType';
import { PosterInterface } from 'type/seasonType';
import { TagInterface } from 'type/tagType';
import { slugify } from 'utils/slugify';
import {
  createDefaultDistributions,
  ENVIRONMENTS,
  getApiServiceName,
  SERVICE_OPTIONS,
  SUBTITLE_LANGUAGES,
} from '../../../../constants/serviceDistribution';
import odkAdminApiService from '../../../../shared/service/odxAdminApiService';
import { I18nService } from '../../../../shared/services/i18n/i18nService';
import { I18nFieldData } from '../../../../shared/types/i18n/i18nTypes';
import posterHandlers from '../../series/components/poster/posterHandlers';
import './SeriesEdit2.css';

dayjs.extend(utc);
dayjs.extend(timezone);

const SeriesEdit2: React.FC = () => {
  const [form] = Form.useForm();
  const { id } = useParams<{ id: string }>();
  const [series, setSeries] = useState<SeriesData | null>(null);
  const [countries, setCountries] = useState<CountryInterface[]>([]);
  const [writeLanguages, setWriteLanguages] = useState<LanguageInterface[]>([]);
  const [searchedPeopleList, setSearchedPeopleList] = useState<PeopleInterface[]>([]);
  const [genreList, setGenreList] = useState<GenreInterface[]>([]);
  const [tagList, setTagList] = useState<TagInterface[]>([]);
  const [enhancedCast, setEnhancedCast] = useState<{ cast: number; type: string }[]>([]);

  // i18n 필드 상태
  const [i18nTitle, setI18nTitle] = useState<I18nFieldData>({});
  const [i18nSynopsis, setI18nSynopsis] = useState<I18nFieldData>({});
  const [i18nSeoTitle, setI18nSeoTitle] = useState<I18nFieldData>({});
  const [i18nSeoSynopsis, setI18nSeoSynopsis] = useState<I18nFieldData>({});
  const [i18nSeoKeyword, setI18nSeoKeyword] = useState<I18nFieldData>({});

  // Poster states
  const [newPosterList, setNewPosterList] = useState<PosterInterface[]>([]);
  const [uploadedPosterList, setUploadedPosterList] = useState<PosterInterface[]>([]);
  const [selectedPoster, setSelectedPoster] = useState<PosterInterface | null>(null);
  const [removedUids, setRemovedUids] = useState<string[]>([]);

  // Service Auto Distribution
  const [distributions, setDistributions] = useState<any[]>([]);

  // 11DB Metadata states
  const [elevenDbSeriesId, setElevenDbSeriesId] = useState<string>('');
  const [elevenDbAwards, setElevenDbAwards] = useState<string>('');
  const [elevenDbAwardsCategory, setElevenDbAwardsCategory] = useState<string>('');
  const [elevenDbRelatedSeries, setElevenDbRelatedSeries] = useState<string>('');
  const [elevenDbRelatedTitles, setElevenDbRelatedTitles] = useState<string>('');
  const [elevenDbLinkedArticles, setElevenDbLinkedArticles] = useState<string>('');
  const [elevenDbUserRatings, setElevenDbUserRatings] = useState<string>('');
  const [elevenDbCriticsRatings, setElevenDbCriticsRatings] = useState<string>('');
  const [elevenDbMovieBoxOffice, setElevenDbMovieBoxOffice] = useState<string>('');

  // 변경된 필드 추적용
  const changedFieldsRef = useRef<Set<string>>(new Set());

  const markFieldChanged = (...fields: string[]) => {
    fields.forEach(field => {
      changedFieldsRef.current.add(field);
    });
  };

  const handleValuesChange = (changedValues: any) => {
    Object.keys(changedValues).forEach(key => {
      markFieldChanged(key);
    });
  };

  // CP from ODK, ODC states
  const [cpFromOdkOptions, setCpFromOdkOptions] = useState<any[]>([]);
  const [cpFromOdcOptions, setCpFromOdcOptions] = useState<any[]>([]);
  const [defaultCpFromOdcOptions, setDefaultCpFromOdcOptions] = useState<any[]>([]);
  const [cpFromOdcLoading, setCpFromOdcLoading] = useState(false);

  const initializeDefaultDistributions = () => {
    setDistributions(createDefaultDistributions());
  };

  const updateDistribution = (serviceName: string, environment: string, updates: any) => {
    // 서비스 자동 배포 관련 필드 변경 플래그
    markFieldChanged('service_auto_distributions');
    markFieldChanged('service_auto_distribution');
    setDistributions(prev =>
      prev.map(dist =>
        dist.service_name === serviceName && dist.environment === environment
          ? { ...dist, ...updates }
          : dist
      )
    );
  };

  // 언어 값을 정규화하는 함수
  const normalizeLanguage = (lang: string | undefined): string | undefined => {
    if (!lang) return undefined;

    const normalized = lang.toUpperCase();

    const mapping: Record<string, string> = {
      KR: 'KO',
      'ZH-CN': 'ZH-HANS',
      'ZH-TW': 'ZH-HANT',
    };

    return mapping[normalized] || normalized;
  };

  const getPosterHandlers = (seriesId: number) => {
    return posterHandlers({
      form,
      seriesId,
      selectedPoster,
      setNewPosterList,
      setUploadedPosterList,
      setSelectedPoster,
      removedUids,
      setRemovedUids,
    });
  };

  const handlePosterUpload = async (targetSeriesId?: number) => {
    const targetId = targetSeriesId || (id && id !== 'new' ? Number(id) : 0);
    if (!targetId) {
      message.error('Please save series first');
      return;
    }
    const handlers = getPosterHandlers(targetId);
    await handlers.handlePosterUpload();
  };

  const handlePosterRemove = (poster: PosterInterface) => {
    if (poster.id && poster.id > 0) {
      // 업로드된 포스터 제거
      // posterList는 원본 데이터로 유지하고, uploadedPosterList에서만 제거
      // 저장 시 posterList와 uploadedPosterList를 비교해서 삭제할 포스터를 찾음
      const currentUploadedList = form.getFieldValue('uploadedPosterList') || [];

      const updatedUploadedList = currentUploadedList.filter(
        (p: PosterInterface) => p.id !== poster.id
      );

      form.setFieldValue('uploadedPosterList', updatedUploadedList);
      setUploadedPosterList(updatedUploadedList);

      // 선택된 포스터가 삭제된 경우 선택 해제
      if (selectedPoster && selectedPoster.id === poster.id) {
        setSelectedPoster(null);
        form.setFieldValue('selectedPoster', null);
      }
    } else {
      // 새로 추가한 포스터 제거
      const currentNewList = form.getFieldValue('newPosterList') || [];
      const updatedNewList = currentNewList.filter((p: PosterInterface) => p.uid !== poster.uid);

      form.setFieldValue('newPosterList', updatedNewList);
      setNewPosterList(updatedNewList);

      if (poster.uid) {
        setRemovedUids(prev => [...prev, poster.uid!]);
      }
    }
  };

  const handleUploadWithLanguageAndOrientation =
    (language: string, orientation: string) => (info: any) => {
      // 파일을 받아서 language와 orientation을 설정한 후 handleChange 호출
      const existingList = form.getFieldValue('newPosterList') || [];
      const existingUids = existingList
        .map((p: PosterInterface) => p.uid)
        .filter((uid: string | undefined): uid is string => !!uid);

      const filtered = (info.fileList || []).filter(
        (file: any) => !removedUids.includes(file.uid) && !existingUids.includes(file.uid)
      );

      // 각 파일에 language와 orientation 추가
      const uploadPosterList = filtered.map((file: any, index: number) => {
        const fileUrl = file.originFileObj ? URL.createObjectURL(file.originFileObj) : '';
        const poster = {
          id: -(existingList.length + index + 1), // 음수 ID로 새 파일임을 표시
          uid: file.uid,
          file: fileUrl,
          is_representative: false,
          state: THUMBNAIL_UPLOAD_STATE.UPLOADING,
          originFileObj: file.originFileObj,
          orientation: orientation,
          language: language, // language는 이미 정규화된 값 (KO, EN, ZH-HANS, VI)
        } as PosterInterface;

        return poster;
      });

      const combined = [...existingList, ...uploadPosterList];
      form.setFieldValue('newPosterList', combined);
      setNewPosterList(combined);
    };

  // 언어별로 그룹화된 포스터 목록
  const POSTER_LANGUAGE_OPTIONS = [
    { value: 'EN', label: 'English', flag: '🇺🇸' },
    { value: 'KO', label: 'Korean', flag: '🇰🇷' },
    { value: 'ZH-HANS', label: 'Simplified Chinese', flag: '🇨🇳' },
    { value: 'VI', label: 'Vietnamese', flag: '🇻🇳' },
  ];

  const POSTER_ORIENTATIONS = [
    { value: 'landscape', label: 'Poster Landscape', dimensions: '1600 x 900' },
    { value: 'portrait', label: 'Poster Portrait', dimensions: '880 x 1280' },
    { value: 'lang_clean', label: 'Clean Poster', dimensions: '1600 x 900' },
    { value: 'square', label: 'Square Poster', dimensions: '1080 x 1080' },
  ];

  const allPosters = useMemo(
    () => [...newPosterList, ...uploadedPosterList],
    [newPosterList, uploadedPosterList]
  );

  const groupedByLanguage = useMemo(() => {
    const grouped: Record<string, Record<string, PosterInterface[]>> = {};
    POSTER_LANGUAGE_OPTIONS.forEach(lang => {
      grouped[lang.value] = {};
      POSTER_ORIENTATIONS.forEach(orientation => {
        grouped[lang.value][orientation.value] = allPosters.filter(
          poster =>
            normalizeLanguage(poster.language) === lang.value &&
            poster.orientation === orientation.value
        );
      });
    });
    return grouped;
  }, [allPosters]);

  useEffect(() => {
    if (id && id !== 'new') {
      fetchSeries();
      fetchElevenDbSeries();
    } else {
      initializeDefaultDistributions();
      form.setFieldValue('posterList', []);
      form.setFieldValue('newPosterList', []);
      form.setFieldValue('uploadedPosterList', []);
    }
    fetchCountries();
    fetchWriteLanguages();
    fetchGenres();
    fetchTags();
    fetchCpFromOdk();
    fetchCpFromOdc();
  }, [id]);

  const fetchElevenDbSeries = async () => {
    const response = await seriesService.fetchElevenDbSeries(Number(id));
    setElevenDbSeriesId(response.eleven_db_id || '');
    setElevenDbAwards(response.awards || '');
    setElevenDbAwardsCategory(response.awards_category || '');
    setElevenDbRelatedSeries(response.related_series || '');
    setElevenDbRelatedTitles(response.related_titles || '');
    setElevenDbLinkedArticles(response.linked_articles || '');
    setElevenDbUserRatings(response.user_ratings || '');
    setElevenDbCriticsRatings(response.critics_ratings || '');
    setElevenDbMovieBoxOffice(response.movie_box_office || '');
  };

  const fetchCpFromOdk = async () => {
    const response = await odkAdminApiService.fetchCpFromOdk();
    setCpFromOdkOptions(response);
  };

  const fetchCpFromOdc = async () => {
    const response = await odkAdminApiService.fetchCpFromOdc();
    setCpFromOdcOptions(response);
    setDefaultCpFromOdcOptions(response);
  };

  // ODC 검색 함수 (debounce 적용)
  const debouncedSearchCpFromOdc = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        setCpFromOdcOptions(defaultCpFromOdcOptions);
        return;
      }

      setCpFromOdcLoading(true);
      try {
        const result = await odkAdminApiService.searchCpFromOdc(searchTerm);
        setCpFromOdcOptions(result);
      } catch (error) {
        console.error('ODC CP search failed:', error);
        setCpFromOdcOptions([]);
      } finally {
        setCpFromOdcLoading(false);
      }
    }, 300),
    [defaultCpFromOdcOptions]
  );

  // ODC Select 박스가 열릴 때 기본 목록 복원
  const handleCpFromOdcDropdownVisibleChange = (open: boolean) => {
    if (open) {
      setCpFromOdcOptions(defaultCpFromOdcOptions);
    }
  };

  // ODC 검색어 변경 처리
  const handleCpFromOdcSearch = (value: string) => {
    if (!value.trim()) {
      setCpFromOdcOptions(defaultCpFromOdcOptions);
    } else {
      debouncedSearchCpFromOdc(value);
    }
  };

  // ODC X 버튼으로 검색어 지웠을 때 처리
  const handleCpFromOdcClear = () => {
    setCpFromOdcOptions(defaultCpFromOdcOptions);
  };

  const fetchSeries = async () => {
    try {
      const data = await seriesService.fetchSeriesWithI18n(Number(id));
      setSeries(data);

      // i18n 필드 초기화
      // i18n_title이 빈 객체가 아니고 값이 있는 경우에만 설정
      if (data.i18n_title) {
        // i18n_title이 객체인지 확인하고, 값이 있는 키가 있는지 확인
        const hasValues = Object.values(data.i18n_title).some(
          value => value !== null && value !== undefined && value !== ''
        );
        if (hasValues) {
          setI18nTitle(data.i18n_title as I18nFieldData);
        } else if (data.title || data.title_en) {
          // i18n_title이 있지만 값이 없으면 레거시 필드로부터 생성
          setI18nTitle({
            en: data.title_en || data.title || '',
            ko: data.title || '',
            'zh-hans': '',
          } as I18nFieldData);
        }
      } else if (data.title || data.title_en) {
        // i18n_title이 없으면 레거시 필드로부터 생성
        setI18nTitle({
          en: data.title_en || data.title || '',
          ko: data.title || '',
          'zh-hans': '',
        } as I18nFieldData);
      }

      // Form 값 설정 (i18n 필드도 포함)
      // 서버에서 받은 UTC 날짜/시간을 PST로 변환해서 표시 (날짜 부분만)
      const formattedData = {
        ...data,
        release_date_start: data.release_date_start
          ? (() => {
              // UTC 날짜/시간을 파싱 (ISO 형식 또는 YYYY-MM-DD 형식 모두 지원)
              let utcDate;
              if (typeof data.release_date_start === 'string') {
                // ISO 형식인지 확인 (예: 2025-11-10T08:00:00.000Z)
                if (
                  data.release_date_start.includes('T') ||
                  data.release_date_start.includes(' ')
                ) {
                  utcDate = dayjs.utc(data.release_date_start);
                } else {
                  // YYYY-MM-DD 형식인 경우
                  utcDate = dayjs.utc(data.release_date_start, 'YYYY-MM-DD');
                }
              } else {
                utcDate = dayjs.utc(data.release_date_start);
              }

              if (!utcDate.isValid()) {
                return null;
              }

              // PST로 변환 후 날짜 부분만 사용
              const pstDate = utcDate.tz('America/Los_Angeles');
              // 날짜 부분만 사용 (YYYY-MM-DD 형식으로 변환 후 다시 파싱)
              return dayjs(pstDate.format('YYYY-MM-DD'), 'YYYY-MM-DD');
            })()
          : null,
        release_date_end: data.release_date_end
          ? (() => {
              // UTC 날짜/시간을 파싱 (ISO 형식 또는 YYYY-MM-DD 형식 모두 지원)
              let utcDate;
              if (typeof data.release_date_end === 'string') {
                // ISO 형식인지 확인 (예: 2025-11-10T08:00:00.000Z)
                if (data.release_date_end.includes('T') || data.release_date_end.includes(' ')) {
                  utcDate = dayjs.utc(data.release_date_end);
                } else {
                  // YYYY-MM-DD 형식인 경우
                  utcDate = dayjs.utc(data.release_date_end, 'YYYY-MM-DD');
                }
              } else {
                utcDate = dayjs.utc(data.release_date_end);
              }

              if (!utcDate.isValid()) {
                return null;
              }

              // PST로 변환 후 날짜 부분만 사용
              const pstDate = utcDate.tz('America/Los_Angeles');
              // 날짜 부분만 사용 (YYYY-MM-DD 형식으로 변환 후 다시 파싱)
              return dayjs(pstDate.format('YYYY-MM-DD'), 'YYYY-MM-DD');
            })()
          : null,
        status: data.status === 'published' ? 'published' : 'unpublished',
        dubbing_mode: data.dubbing_mode || 'disabled',
        produce_year: (data as any).produce_year,
        // 백엔드에서 genres 또는 categories로 올 수 있음, 폼에서는 genres로 사용
        genres: (data as any).genres || data.categories || [],
        // i18n_title 값을 Form 필드에 설정
        series_title_en: data.i18n_title?.en || data.title_en || data.title || '',
        series_title_ko: data.i18n_title?.ko || '',
        series_title_zh_hans: data.i18n_title?.['zh-hans'] || '',
        series_title_vi: (data.i18n_title as any)?.vi || '',
      };
      form.setFieldsValue(formattedData);
      if (data.i18n_synopsis && Object.keys(data.i18n_synopsis).length > 0) {
        setI18nSynopsis(data.i18n_synopsis);
      } else if (data.synopsis || data.synopsis_en) {
        // i18n_synopsis가 없으면 레거시 필드로부터 생성
        setI18nSynopsis({
          en: data.synopsis_en || data.synopsis || '',
          ko: data.synopsis || '',
          'zh-hans': '',
          vi: '',
        } as I18nFieldData);
      }
      if (data.i18n_seo_title && Object.keys(data.i18n_seo_title).length > 0) {
        setI18nSeoTitle(data.i18n_seo_title);
      }
      if (data.i18n_seo_synopsis && Object.keys(data.i18n_seo_synopsis).length > 0) {
        setI18nSeoSynopsis(data.i18n_seo_synopsis);
      }
      if (data.i18n_seo_keyword && Object.keys(data.i18n_seo_keyword).length > 0) {
        setI18nSeoKeyword(data.i18n_seo_keyword);
      }

      // Enhanced Cast 초기화
      if (data.enhanced_cast) {
        setEnhancedCast(
          data.enhanced_cast.map(item => ({
            cast: item.cast,
            type: item.type,
          }))
        );
        // Enhanced Cast에 있는 cast ID들에 대한 people 정보 가져오기
        const castIds = data.enhanced_cast.map(item => item.cast);
        if (castIds.length > 0) {
          try {
            const peopleResult = await peopleApiService.fetchByIds(castIds);
            if (peopleResult.results && peopleResult.results.length > 0) {
              setSearchedPeopleList(prev => {
                const existingIds = new Set(prev.map(p => p.id));
                const newPeople = peopleResult.results.filter(p => !existingIds.has(p.id));
                return [...prev, ...newPeople];
              });
            }
          } catch (error) {
            console.error('Failed to fetch people for enhanced cast:', error);
          }
        }
      }

      // Running Time 설정은 Country에서 가져오지 않음 (CountryInterface에 running_time 속성이 없음)

      // Service Auto Distribution 설정
      if (
        (data as any).service_auto_distributions &&
        (data as any).service_auto_distributions.length > 0
      ) {
        const apiDistributions = (data as any).service_auto_distributions;
        const formattedDistributions: any[] = [];

        SERVICE_OPTIONS.forEach(service => {
          ENVIRONMENTS.forEach(env => {
            const apiServiceName = getApiServiceName(service.name, env);
            const apiDist = apiDistributions.find((d: any) => d.service_name === apiServiceName);

            if (apiDist) {
              formattedDistributions.push({
                id: apiDist.id,
                service_name: service.name,
                environment: env,
                enabled: true,
                auto_publish: apiDist.auto_publish,
                subtitle_langs:
                  apiDist.subtitle_langs ||
                  (apiDist.subtitles ? apiDist.subtitles.map((s: any) => s.subtitle_lang) : []),
              });
            } else {
              formattedDistributions.push({
                service_name: service.name,
                environment: env,
                enabled: false,
                auto_publish: false,
                subtitle_langs: [],
              });
            }
          });
        });

        setDistributions(formattedDistributions);
      } else {
        initializeDefaultDistributions();
      }

      // Posters 설정
      try {
        const posterList = await seriesService.fetchPoster(Number(id));
        if (posterList && posterList.length > 0) {
          const uploadedPosterList: PosterInterface[] = posterList.map(
            (poster: PosterInterface) => {
              return {
                ...poster,
                state: THUMBNAIL_UPLOAD_STATE.UPLOADED,
                originFileObj: undefined,
                language: normalizeLanguage(poster.language),
              };
            }
          );

          const selectedPoster = uploadedPosterList.find(
            (poster: PosterInterface) => poster.is_representative
          );

          form.setFieldValue('newPosterList', []);
          form.setFieldValue('uploadedPosterList', uploadedPosterList);
          form.setFieldValue('posterList', uploadedPosterList);
          setNewPosterList([]);
          setUploadedPosterList(uploadedPosterList);
          setSelectedPoster(selectedPoster || null);
        } else {
          form.setFieldValue('newPosterList', []);
          form.setFieldValue('uploadedPosterList', []);
          form.setFieldValue('posterList', []);
          setNewPosterList([]);
          setUploadedPosterList([]);
          setSelectedPoster(null);
        }
      } catch (error) {
        console.error('Failed to fetch posters:', error);
        form.setFieldValue('newPosterList', []);
        form.setFieldValue('uploadedPosterList', []);
        form.setFieldValue('posterList', []);
        setNewPosterList([]);
        setUploadedPosterList([]);
        setSelectedPoster(null);
      }

      // Genres와 Tags 가져오기
      const genreIds = (data as any).genres || data.categories || [];
      if (genreIds.length > 0) {
        try {
          const genreResponse = await genreApiService.fetchByIds(genreIds);
          setGenreList(genreResponse.results);
        } catch (error) {
          console.error('Failed to fetch genres:', error);
        }
      }
      if ((data as any).tags && (data as any).tags.length > 0) {
        try {
          // tags는 ID 배열이거나 Tag 객체 배열일 수 있음
          const tagIds = (data as any).tags.map((t: any) => (typeof t === 'number' ? t : t.id));
          const tagResponse = await tagApiService.fetchByIds(tagIds);
          setTagList(tagResponse.results);
        } catch (error) {
          console.error('Failed to fetch tags:', error);
        }
      }
    } catch (error) {
      console.error('Failed to fetch series:', error);
      message.error('Failed to load series data');
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await globalSettingService.fetchCountries(
        new URLSearchParams({ page_size: '1000' })
      );
      setCountries(response.results);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchWriteLanguages = async () => {
    try {
      const response = await globalSettingService.fetchLanguages(
        new URLSearchParams({
          type: 'write',
          page_size: '100',
          order_by: 'order',
          order_direction: 'asc',
        })
      );
      setWriteLanguages(response.results);
    } catch (error) {
      console.error('Failed to fetch write languages:', error);
    }
  };

  const fetchGenres = async () => {
    try {
      // Not setting genreList here, only for search
    } catch (error) {
      console.error('Failed to fetch genres:', error);
    }
  };

  const fetchTags = async () => {
    try {
      // Not setting tagList here, only for search
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const handleTitleChange = (value: I18nFieldData) => {
    setI18nTitle(value);
    // Auto-generate slug from title_en
    if (value.en) {
      const slug = slugify(value.en);
      form.setFieldValue('slug', slug);
      markFieldChanged('slug');
    }
    markFieldChanged('i18n_title', 'title', 'title_en');
  };

  const handleCountryOfOriginChange = async (countryId: number) => {
    form.setFieldValue('produce_country', countryId);
    // Running Time은 Country에서 가져오지 않음 (CountryInterface에 running_time 속성이 없음)
  };

  const handleAutoFillSeo = (type: 'title' | 'keyword' | 'synopsis') => {
    if (type === 'title') {
      // zh-HK는 zh-Hant 또는 zh-Hans에서 가져오기
      const seoTitle = { ...i18nTitle } as any;
      if (!seoTitle['zh-hk']) {
        // zh-hant가 있으면 zh-hant 사용, 없으면 zh-hans 사용
        if (i18nTitle['zh-hant']) {
          seoTitle['zh-hk'] = i18nTitle['zh-hant'];
        } else if (i18nTitle['zh-hans']) {
          seoTitle['zh-hk'] = i18nTitle['zh-hans'];
        }
      }
      setI18nSeoTitle(seoTitle);
      markFieldChanged('i18n_seo_title');
    } else if (type === 'keyword') {
      // zh-HK는 zh-Hant 또는 zh-Hans에서 가져오기
      const seoKeyword = { ...i18nTitle } as any;
      if (!seoKeyword['zh-hk']) {
        // zh-hant가 있으면 zh-hant 사용, 없으면 zh-hans 사용
        if (i18nTitle['zh-hant']) {
          seoKeyword['zh-hk'] = i18nTitle['zh-hant'];
        } else if (i18nTitle['zh-hans']) {
          seoKeyword['zh-hk'] = i18nTitle['zh-hans'];
        }
      }
      setI18nSeoKeyword(seoKeyword);
      markFieldChanged('i18n_seo_keyword');
    } else if (type === 'synopsis') {
      // zh-HK는 zh-Hant 또는 zh-Hans에서 가져오기
      const seoSynopsis = { ...i18nSynopsis } as any;
      if (!seoSynopsis['zh-hk']) {
        // zh-hant가 있으면 zh-hant 사용, 없으면 zh-hans 사용
        if (i18nSynopsis['zh-hant']) {
          seoSynopsis['zh-hk'] = i18nSynopsis['zh-hant'];
        } else if (i18nSynopsis['zh-hans']) {
          seoSynopsis['zh-hk'] = i18nSynopsis['zh-hans'];
        }
      }
      setI18nSeoSynopsis(seoSynopsis);
      markFieldChanged('i18n_seo_synopsis');
    }
  };

  const searchPeopleForEnhancedCast = async (search: string) => {
    try {
      const response = await peopleApiService.fetch(
        new URLSearchParams({ search, page: '1', page_size: '100' })
      );
      setSearchedPeopleList(response.results);
      return response.results;
    } catch (error) {
      console.error('Failed to search people:', error);
      return [];
    }
  };

  const handleSave = async (saveToSeason1: boolean = false) => {
    try {
      await form.validateFields();
      const formData = form.getFieldsValue();

      // Prepare i18n data in correct structure (Movie와 동일한 방식)
      const i18nStructuredData = {
        ...formData,
        i18n: {
          title: i18nTitle,
          synopsis: i18nSynopsis,
          seo_title: i18nSeoTitle,
          seo_synopsis: i18nSeoSynopsis,
          seo_keyword: i18nSeoKeyword,
        },
      };

      // Sync i18n data with legacy data (I18nService 사용)
      const finalFormData = I18nService.processMixedFormatData(i18nStructuredData);

      // Create API request data (including i18n fields)
      const apiRequest = I18nService.createApiRequest(finalFormData);

      // Ensure i18n fields are initialized as empty objects if undefined
      // Remove series_title_* fields and use title/title_en from apiRequest
      const restFormData = { ...formData };
      delete restFormData.series_title_en;
      delete restFormData.series_title_ko;
      delete restFormData.series_title_zh_hans;
      delete restFormData.series_title_vi;
      const productionYear = formData.produce_year; // Form 필드명이 produce_year로 변경됨
      delete restFormData.produce_year;
      // genres와 tags는 변경된 경우에만 포함되도록 restFormData에서 제거
      // form.getFieldValue를 직접 사용하여 최신 값을 가져옴
      const genresValue = form.getFieldValue('genres') || [];
      const tagsValue = form.getFieldValue('tags') || [];
      delete restFormData.genres;
      delete restFormData.categories; // 하위 호환성을 위해 제거
      delete restFormData.tags;

      const fullSeriesData: SeriesApiRequest = {
        ...restFormData,
        // This includes title, title_en, synopsis, synopsis_en, seo_title, seo_synopsis, seo_keyword, i18n_*
        ...apiRequest,
        enhanced_cast: enhancedCast,
        title: restFormData.title || restFormData.title_en,
        release_date_start: formData.release_date_start
          ? (() => {
              // 사용자가 선택한 날짜를 PST 00:00:00으로 해석
              const selectedDate =
                typeof formData.release_date_start === 'string'
                  ? dayjs(formData.release_date_start, 'YYYY-MM-DD')
                  : dayjs(formData.release_date_start);

              if (!selectedDate.isValid()) {
                return null;
              }

              // PST 00:00:00으로 설정 후 UTC로 변환
              const pstDate = selectedDate.tz('America/Los_Angeles', true).startOf('day');
              const utcDate = pstDate.utc();

              // UTC 날짜와 시간을 ISO 형식으로 반환 (예: 2025-11-10T08:00:00.000Z)
              return utcDate.toISOString();
            })()
          : null,
        release_date_end: formData.release_date_end
          ? (() => {
              // 사용자가 선택한 날짜를 PST 00:00:00으로 해석
              const selectedDate =
                typeof formData.release_date_end === 'string'
                  ? dayjs(formData.release_date_end, 'YYYY-MM-DD')
                  : dayjs(formData.release_date_end);

              if (!selectedDate.isValid()) {
                return null;
              }

              // PST 00:00:00으로 설정 후 UTC로 변환
              const pstDate = selectedDate.tz('America/Los_Angeles', true).startOf('day');
              const utcDate = pstDate.utc();

              // UTC 날짜와 시간을 ISO 형식으로 반환 (예: 2025-11-10T08:00:00.000Z)
              return utcDate.toISOString();
            })()
          : null,
        status:
          formData.status === true || formData.status === 'published' ? 'published' : 'unpublished',
        dubbing_mode: formData.dubbing_mode || 'disabled',
        service_auto_distribution: distributions,
        produce_year: productionYear ? Number(productionYear) : undefined,
        // CP from ODK, ODC, ODX, country_of_origin - undefined일 때 null로 명시적으로 설정 (서버에서 업데이트되도록)
        country_of_origin:
          formData.country_of_origin !== undefined ? formData.country_of_origin : null,
        cp_from_odk: formData.cp_from_odk !== undefined ? formData.cp_from_odk : null,
        cp_from_odc: formData.cp_from_odc !== undefined ? formData.cp_from_odc : null,
        cp_from_odx: formData.cp_from_odx !== undefined ? formData.cp_from_odx : null,
        i18n_title: i18nTitle || {},
        i18n_synopsis: i18nSynopsis || {},
        i18n_seo_title: i18nSeoTitle || {},
        i18n_seo_synopsis: i18nSeoSynopsis || {},
        i18n_seo_keyword: i18nSeoKeyword || {},
        service_auto_distributions: distributions
          .filter(dist => dist.enabled)
          .map(dist => ({
            service_name: getApiServiceName(dist.service_name, dist.environment),
            auto_publish: dist.auto_publish,
            subtitle_langs: dist.subtitle_langs || [],
          })),
        genres: genresValue || [],
        tags: tagsValue || [],
      };

      const elevenDbSeriesData: ElevenDbSeriesInterface = {
        series: Number(id),
        eleven_db_id: elevenDbSeriesId,
        awards: elevenDbAwards,
        awards_category: elevenDbAwardsCategory,
        related_series: elevenDbRelatedSeries,
        related_titles: elevenDbRelatedTitles,
        linked_articles: elevenDbLinkedArticles,
        user_ratings: elevenDbUserRatings,
        critics_ratings: elevenDbCriticsRatings,
        movie_box_office: elevenDbMovieBoxOffice,
      };

      if (id && id !== 'new') {
        // 변경된 필드만 추출해서 업데이트
        const seriesDataForUpdate: Partial<SeriesApiRequest> = {};
        changedFieldsRef.current.forEach(field => {
          if (field === 'genres') {
            // genres는 변경된 경우에만 포함
            (seriesDataForUpdate as any)[field] = genresValue || [];
          } else if (field === 'tags') {
            // tags는 변경된 경우에만 포함
            (seriesDataForUpdate as any)[field] = tagsValue || [];
          } else if ((fullSeriesData as any)[field] !== undefined) {
            (seriesDataForUpdate as any)[field] = (fullSeriesData as any)[field];
          }
        });

        if (Object.keys(seriesDataForUpdate).length > 0) {
          await seriesService.updateSeriesWithI18n(Number(id), seriesDataForUpdate);
          message.success('Series updated successfully');
        }
        await seriesService.updateElevenDbSeries(Number(id), elevenDbSeriesData);
        // Poster 업로드 처리
        // saveToSeason1이 true일 때는 리로드를 하지 않도록 포스터 업로드를 직접 처리
        let uploadedPostersForSeason: PosterInterface[] = [];

        // 포스터 변경사항 확인 (새로 추가, 삭제, 변경)
        const posterList = form.getFieldValue('posterList') || [];
        const uploadedPosterList = form.getFieldValue('uploadedPosterList') || [];
        const hasPosterChanges =
          newPosterList.length > 0 ||
          removedUids.length > 0 ||
          posterList.length !== uploadedPosterList.length ||
          posterList.some(
            (p: PosterInterface) =>
              !uploadedPosterList.some((up: PosterInterface) => up.id === p.id)
          );

        if (hasPosterChanges) {
          if (saveToSeason1) {
            // saveToSeason1일 때는 리로드 없이 직접 업로드 처리
            const selectedPoster = form.getFieldValue('selectedPoster');

            // 중복 체크
            const allList = [...newPosterList, ...uploadedPosterList];
            const comboCounts = allList.reduce<Record<string, number>>((acc, t) => {
              const key = `${t.language}-${t.orientation}`;
              acc[key] = (acc[key] || 0) + 1;
              return acc;
            }, {});
            const duplicates = Object.entries(comboCounts)
              .filter(([_, cnt]) => cnt > 1)
              .map(([combo]) => combo);

            if (duplicates.length > 0) {
              message.error(`중복된 언어/화면비 조합: ${duplicates.join(', ')}`);
            } else {
              try {
                if (newPosterList.length > 0) {
                  await seriesService.uploadPoster(Number(id), newPosterList);
                  uploadedPostersForSeason = [...newPosterList]; // 시즌에 복사할 포스터 저장
                }
                const removePosters = posterList.filter(
                  (uploadedPoster: { id: number }) =>
                    !uploadedPosterList.some(
                      (uploadPoster: { id: number }) => uploadPoster.id === uploadedPoster.id
                    )
                );
                if (removePosters.length > 0) {
                  await seriesService.removePoster(Number(id), removePosters);
                }
                const changedPosters = uploadedPosterList.filter((updated: PosterInterface) => {
                  const original = posterList.find((t: PosterInterface) => t.id === updated.id);
                  return (
                    original &&
                    (original.language !== updated.language ||
                      original.orientation !== updated.orientation ||
                      original.is_representative !== updated.is_representative)
                  );
                });
                if (changedPosters.length > 0) {
                  for (const poster of changedPosters) {
                    await seriesService.updatePoster(Number(id), poster);
                  }
                }
                if (selectedPoster && selectedPoster.id !== -1) {
                  await seriesService.updatePoster(Number(id), selectedPoster);
                }
                message.success('Posters uploaded successfully');
              } catch (error) {
                console.error('Failed to upload posters:', error);
                message.error('Failed to upload posters');
              }
            }
          } else {
            // saveToSeason1이 false일 때는 기존대로 리로드 포함
            await handlePosterUpload();
          }
        }

        if (saveToSeason1) {
          // Save to Season 1 로직 - 기존 시리즈의 시즌 1 생성/업데이트
          try {
            const seriesId = Number(id);
            const seriesSlug = formData.slug || series?.slug || '';

            const hasSeriesChanged = (...fields: string[]) =>
              fields.some(f => changedFieldsRef.current.has(f));

            const seasonUpdateData: any = {};

            // Title 관련 변경이 있는 경우에만 시즌 타이틀 업데이트
            if (hasSeriesChanged('title', 'title_en', 'i18n_title')) {
              const seasonI18nTitle: I18nFieldData = {};
              if (i18nTitle.en) {
                seasonI18nTitle.en = `${i18nTitle.en}`;
              }
              if (i18nTitle.ko) {
                seasonI18nTitle.ko = `${i18nTitle.ko}`;
              }
              if (i18nTitle['zh-hans']) {
                seasonI18nTitle['zh-hans'] = `${i18nTitle['zh-hans']}`;
              }
              if (i18nTitle['zh-hant']) {
                seasonI18nTitle['zh-hant'] = `${i18nTitle['zh-hant']}`;
              }
              if ((i18nTitle as any).vi) {
                (seasonI18nTitle as any).vi = `${(i18nTitle as any).vi}`;
              }

              seasonUpdateData.i18n_title = seasonI18nTitle;
              seasonUpdateData.title =
                seasonI18nTitle.en || `${apiRequest.title_en || apiRequest.title || ''}`;
              seasonUpdateData.title_en = seasonI18nTitle.en || `${apiRequest.title_en || ''}`;
            }

            // Synopsis 변경 시에만 반영
            if (hasSeriesChanged('synopsis', 'synopsis_en', 'i18n_synopsis')) {
              seasonUpdateData.i18n_synopsis = i18nSynopsis || {};
              seasonUpdateData.synopsis = apiRequest.synopsis;
              seasonUpdateData.synopsis_en = apiRequest.synopsis_en;
            }

            // SEO 필드 변경 시에만 반영
            if (hasSeriesChanged('seo_title', 'i18n_seo_title')) {
              seasonUpdateData.i18n_seo_title = i18nSeoTitle || {};
              seasonUpdateData.seo_title = apiRequest.seo_title;
            }
            if (hasSeriesChanged('seo_synopsis', 'i18n_seo_synopsis')) {
              seasonUpdateData.i18n_seo_synopsis = i18nSeoSynopsis || {};
              seasonUpdateData.seo_synopsis = apiRequest.seo_synopsis;
            }
            if (hasSeriesChanged('seo_keyword', 'i18n_seo_keyword')) {
              seasonUpdateData.i18n_seo_keyword = i18nSeoKeyword || {};
              seasonUpdateData.seo_keyword = apiRequest.seo_keyword;
            }

            // 날짜 변경 시에만 반영
            if (hasSeriesChanged('release_date_start')) {
              seasonUpdateData.release_date_start = fullSeriesData.release_date_start;
            }
            if (hasSeriesChanged('release_date_end')) {
              seasonUpdateData.release_date_end = fullSeriesData.release_date_end;
            }

            // 상태, 더빙 모드
            if (hasSeriesChanged('status')) {
              seasonUpdateData.status = fullSeriesData.status;
            }
            if (hasSeriesChanged('dubbing_mode')) {
              seasonUpdateData.dubbing_mode = fullSeriesData.dubbing_mode;
            }

            if (hasSeriesChanged('cp_from_odk')) {
              seasonUpdateData.cp_from_odk =
                formData.cp_from_odk !== undefined ? formData.cp_from_odk : null;
            }
            if (hasSeriesChanged('cp_from_odc')) {
              seasonUpdateData.cp_from_odc =
                formData.cp_from_odc !== undefined ? formData.cp_from_odc : null;
            }
            if (hasSeriesChanged('cp_from_odx')) {
              seasonUpdateData.cp_from_odx =
                formData.cp_from_odx !== undefined ? formData.cp_from_odx : null;
            }

            // Service Auto Distribution
            if (hasSeriesChanged('service_auto_distributions')) {
              seasonUpdateData.service_auto_distribution = distributions;
              seasonUpdateData.service_auto_distributions =
                fullSeriesData.service_auto_distributions;
            }

            // 연도, 국가, 연령 등급, Enhanced Cast
            if (hasSeriesChanged('produce_year')) {
              seasonUpdateData.production_year = productionYear
                ? Number(productionYear)
                : undefined;
            }
            if (hasSeriesChanged('country_of_origin')) {
              seasonUpdateData.country_of_origin = formData.country_of_origin;
            }
            if (hasSeriesChanged('age_rating')) {
              seasonUpdateData.age_rating = formData.age_rating;
            }
            if (hasSeriesChanged('enhanced_cast')) {
              seasonUpdateData.enhanced_cast = enhancedCast;
            }

            if (hasSeriesChanged('genres')) {
              seasonUpdateData.genres = genresValue || [];
            }
            if (hasSeriesChanged('tags')) {
              seasonUpdateData.tags = tagsValue || [];
            }

            // 기존 시즌 1이 있는지 확인 (같은 series_id와 season_order=1)
            const existingSeasons =
              await seriesSeasonItemService.fetchChildItemsByParentId(seriesId);
            const existingSeason1 = existingSeasons.find((s: any) => s.season_order === 1);

            if (existingSeason1) {
              // 변경된 시즌 데이터가 있을 때만 업데이트 실행
              if (Object.keys(seasonUpdateData).length > 0) {
                await seasonService.updateSeasonWithI18n(existingSeason1.id, seasonUpdateData);
                message.success('Season 1 updated successfully');
              }

              // 시리즈 포스터를 시즌에도 복사
              if (uploadedPostersForSeason.length > 0) {
                try {
                  // 새로 업로드된 포스터를 시즌에도 업로드
                  await seasonService.uploadPoster(existingSeason1.id, uploadedPostersForSeason);
                  message.success('Series posters copied to Season 1');
                } catch (error) {
                  console.error('Failed to copy posters to Season 1:', error);
                  message.warning('Season 1 updated but failed to copy some posters');
                }
              }

              // 변경된 포스터가 있으면 시즌에도 업데이트
              const changedPosters = uploadedPosterList.filter((updated: PosterInterface) => {
                const original = (form.getFieldValue('posterList') || []).find(
                  (t: PosterInterface) => t.id === updated.id
                );
                return (
                  original &&
                  (original.language !== updated.language ||
                    original.orientation !== updated.orientation ||
                    original.is_representative !== updated.is_representative)
                );
              });

              if (changedPosters.length > 0) {
                try {
                  for (const poster of changedPosters) {
                    await seasonService.updatePoster(existingSeason1.id, poster);
                  }
                } catch (error) {
                  console.error('Failed to update posters in Season 1:', error);
                }
              }
            } else {
              // 새 시즌 1 생성
              const seasonI18nTitle: I18nFieldData = {};
              if (i18nTitle.en) {
                seasonI18nTitle.en = `${i18nTitle.en}`;
              }
              if (i18nTitle.ko) {
                seasonI18nTitle.ko = `${i18nTitle.ko}`;
              }
              if (i18nTitle['zh-hans']) {
                seasonI18nTitle['zh-hans'] = `${i18nTitle['zh-hans']}`;
              }
              if (i18nTitle['zh-hant']) {
                seasonI18nTitle['zh-hant'] = `${i18nTitle['zh-hant']}`;
              }
              if ((i18nTitle as any).vi) {
                (seasonI18nTitle as any).vi = `${(i18nTitle as any).vi}`;
              }

              const seasonSlug = seriesSlug ? `${seriesSlug}-season-1` : `season-1-${Date.now()}`;

              const seasonData: any = {
                series_id: seriesId,
                series: seriesId,
                season_order: 1,
                slug: seasonSlug,
                title: seasonI18nTitle.en || `${apiRequest.title_en || apiRequest.title || ''}`,
                title_en: seasonI18nTitle.en || `${apiRequest.title_en || ''}`,
                i18n_title: seasonI18nTitle,
                i18n_synopsis: i18nSynopsis || {},
                i18n_seo_title: i18nSeoTitle || {},
                i18n_seo_synopsis: i18nSeoSynopsis || {},
                i18n_seo_keyword: i18nSeoKeyword || {},
                synopsis: apiRequest.synopsis,
                synopsis_en: apiRequest.synopsis_en,
                seo_title: apiRequest.seo_title,
                seo_synopsis: apiRequest.seo_synopsis,
                seo_keyword: apiRequest.seo_keyword,
                release_date_start: fullSeriesData.release_date_start,
                release_date_end: fullSeriesData.release_date_end,
                status: fullSeriesData.status,
                dubbing_mode: fullSeriesData.dubbing_mode,
                service_auto_distribution: distributions,
                production_year: productionYear ? Number(productionYear) : undefined,
                country_of_origin: formData.country_of_origin,
                enhanced_cast: enhancedCast,
                age_rating: formData.age_rating,
                cp_from_odk: formData.cp_from_odk !== undefined ? formData.cp_from_odk : null,
                cp_from_odc: formData.cp_from_odc !== undefined ? formData.cp_from_odc : null,
                cp_from_odx: formData.cp_from_odx !== undefined ? formData.cp_from_odx : null,
              };

              const newSeason = await seasonService.createSeasonWithI18n(seasonData);
              message.success('Season 1 created successfully');

              // 시리즈 포스터를 시즌에도 복사
              if (uploadedPostersForSeason.length > 0) {
                try {
                  await seasonService.uploadPoster(newSeason.id, uploadedPostersForSeason);
                  message.success('Series posters copied to Season 1');
                } catch (error) {
                  console.error('Failed to copy posters to Season 1:', error);
                  message.warning('Season 1 created but failed to copy some posters');
                }
              }
            }
          } catch (error: any) {
            console.error('Failed to save/create Season 1:', error);
            message.warning('Series saved but failed to save/create Season 1');
          }
        }

        // 모든 업데이트 완료 후 리로드
        window.location.reload();
      } else {
        const created = await seriesService.createSeriesWithI18n(fullSeriesData);
        await seriesService.createElevenDbSeries(created.id, {
          ...elevenDbSeriesData,
          series: Number(created.id),
        } as ElevenDbSeriesInterface);
        message.success('Series created successfully');

        // Poster 업로드 처리 - 신규 생성 시에도 포스터 저장
        if (newPosterList.length > 0) {
          try {
            await handlePosterUpload(created.id);
            message.success('Posters uploaded successfully');
          } catch (error) {
            console.error('Failed to upload posters:', error);
            message.warning('Series created but failed to upload some posters');
          }
        }

        if (saveToSeason1) {
          // Create Season 1 로직 - 신규 시리즈의 시즌 1 생성
          try {
            const seriesId = created.id;
            if (!seriesId) {
              message.error('Series ID is missing. Cannot create Season 1.');
              return;
            }
            const seriesSlug = formData.slug || created.slug || '';

            // 시즌 1의 title 생성 (각 언어별로)
            const seasonI18nTitle: I18nFieldData = {};
            if (i18nTitle.en) {
              seasonI18nTitle.en = `${i18nTitle.en}`;
            }
            if (i18nTitle.ko) {
              seasonI18nTitle.ko = `${i18nTitle.ko}`;
            }
            if (i18nTitle['zh-hans']) {
              seasonI18nTitle['zh-hans'] = `${i18nTitle['zh-hans']}`;
            }
            if (i18nTitle['zh-hant']) {
              seasonI18nTitle['zh-hant'] = `${i18nTitle['zh-hant']}`;
            }
            if ((i18nTitle as any).vi) {
              (seasonI18nTitle as any).vi = `${(i18nTitle as any).vi}`;
            }

            // 시즌 slug 생성
            const seasonSlug = seriesSlug ? `${seriesSlug}-season-1` : `season-1-${Date.now()}`;

            const seasonData: any = {
              series_id: seriesId,
              series: seriesId, // 백엔드가 series 필드를 기대할 수도 있음 (Episode와 동일하게)
              season_order: 1,
              slug: seasonSlug,
              title: seasonI18nTitle.en || `${apiRequest.title_en || apiRequest.title || ''}`,
              title_en: seasonI18nTitle.en || `${apiRequest.title_en || ''}`,
              i18n_title: seasonI18nTitle,
              i18n_synopsis: i18nSynopsis || {},
              i18n_seo_title: i18nSeoTitle || {},
              i18n_seo_synopsis: i18nSeoSynopsis || {},
              i18n_seo_keyword: i18nSeoKeyword || {},
              synopsis: apiRequest.synopsis,
              synopsis_en: apiRequest.synopsis_en,
              seo_title: apiRequest.seo_title,
              seo_synopsis: apiRequest.seo_synopsis,
              seo_keyword: apiRequest.seo_keyword,
              release_date_start: fullSeriesData.release_date_start,
              release_date_end: fullSeriesData.release_date_end,
              status: fullSeriesData.status,
              dubbing_mode: fullSeriesData.dubbing_mode,
              service_auto_distribution: distributions,
              production_year: productionYear ? Number(productionYear) : undefined,
              country_of_origin: formData.country_of_origin,
              enhanced_cast: enhancedCast,
              age_rating: formData.age_rating,
              cp_from_odk: formData.cp_from_odk !== undefined ? formData.cp_from_odk : null,
              cp_from_odc: formData.cp_from_odc !== undefined ? formData.cp_from_odc : null,
              cp_from_odx: formData.cp_from_odx !== undefined ? formData.cp_from_odx : null,
              genres: genresValue || [],
              tags: tagsValue || [],
            };

            const newSeason = await seasonService.createSeasonWithI18n(seasonData);
            message.success('Season 1 created successfully');

            // 시리즈 포스터를 시즌에도 복사
            if (newPosterList.length > 0) {
              try {
                await seasonService.uploadPoster(newSeason.id, newPosterList);
                message.success('Series posters copied to Season 1');
              } catch (error) {
                console.error('Failed to copy posters to Season 1:', error);
                message.warning('Season 1 created but failed to copy some posters');
              }
            }
          } catch (error: any) {
            console.error('Failed to create Season 1:', error);
            console.error('Error response:', error?.response?.data);
            message.warning('Series created but failed to create Season 1');
          }
        }

        window.location.href = `/series/edit2/${created.id}`;
      }
    } catch (error: any) {
      console.error('Failed to save series:', error);
      message.error(error?.response?.data?.detail || 'Failed to save series');
    }
  };

  const tabs = useMemo<CustomSelectorTabInterface<any>[]>(() => {
    const genreTab: CustomSelectorTabInterface<GenreInterface> = {
      label: 'Genre',
      selected: genreList.filter(g => {
        // 백엔드에서 genres 또는 categories로 올 수 있음, 폼에서는 genres로 사용
        const genreIds =
          form.getFieldValue('genres') || (series as any)?.genres || series?.categories || [];
        return genreIds.includes(g.id);
      }),
      setSelected: (selected: GenreInterface[]) => {
        const genreIds = selected.map(g => g.id);
        form.setFieldValue('genres', genreIds);
        setGenreList(selected);
        markFieldChanged('genres');
      },
      fetchSearch: (search: string) =>
        genreApiService
          .fetch(new URLSearchParams({ search, page: '1', page_size: '100' }))
          .then(res => res.results),
      getLabel: (genre: GenreInterface) => genre.slug || genre.i18n_name?.ko || '',
    };

    const tagTab: CustomSelectorTabInterface<TagInterface> = {
      label: 'Tag',
      selected: tagList.filter(t => {
        const tagIds = form.getFieldValue('tags') || (series as any)?.tags || [];
        return tagIds.includes(t.id);
      }),
      setSelected: (selected: TagInterface[]) => {
        const tagIds = selected.map(t => t.id);
        form.setFieldValue('tags', tagIds);
        setTagList(selected);
        markFieldChanged('tags');
      },
      fetchSearch: (search: string) =>
        tagApiService
          .fetch(new URLSearchParams({ search, page: '1', page_size: '100' }))
          .then(res => res.results),
      getLabel: (tag: TagInterface) => tag.name || tag.i18n_name?.ko || '',
    };

    return [genreTab, tagTab];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genreList, tagList, series]);

  // Seasons Transfer columns
  const transferColumns: TableColumnsType<TransferDataSourceInterface> = [
    {
      dataIndex: 'id',
      title: 'ID',
      render: (id: number) => (
        <span>
          <Button type="link" onClick={() => window.open(`/season/edit2/${id}`, '_blank')}>
            {id}
          </Button>
        </span>
      ),
    },
    {
      dataIndex: 'name',
      title: 'Name',
    },
    {
      dataIndex: 'season_order',
      title: 'Season Number',
    },
    // {
    //   dataIndex: 'status',
    //   title: 'Status',
    //   render: (status: string) => {
    //     const statusMap: Record<string, { color: string; text: string }> = {
    //       published: { color: '#52c41a', text: 'enabled' },
    //       unpublished: { color: '#ff4d4f', text: 'disabled' },
    //     };
    //     const statusInfo = statusMap[status] || { color: '#999', text: status || 'disabled' };
    //     return <span style={{ color: statusInfo.color }}>{statusInfo.text}</span>;
    //   },
    // },
  ];

  const seasonDataHandler: TransferDataHandlerInterface = {
    fetchSourceData: async (
      query: Record<string, string>
    ): Promise<{ data: TransferDataSourceInterface[]; count: number }> => {
      const queryParams = new URLSearchParams(query);
      queryParams.append('order_by', 'season_order');
      const seasons = await seasonService.fetch(queryParams);
      const transferDataSource = seasons.results.map(season => ({
        id: season.id || 0,
        key: season.id || 0,
        name: season.slug || season.i18n_title?.en || '',
        season_order: season.season_order || 0,
        disabled: false,
        status: season.status
          ? season.status === 'published'
            ? 'Enabled'
            : 'Disabled'
          : 'Disabled',
      }));
      return {
        data: transferDataSource,
        count: seasons.count,
      };
    },
    fetchTargetData: async (): Promise<{ data: TransferDataSourceInterface[]; count: number }> => {
      if (!id || id === 'new') {
        return { data: [], count: 0 };
      }
      const queryParams = new URLSearchParams();
      queryParams.append('order_by', 'season_order');
      queryParams.append('order_direction', 'asc');
      const seasons = await seriesSeasonItemService.fetchChildItemsByParentId(
        Number(id),
        queryParams
      );

      return {
        data: seasons.map(season => ({
          id: season.id || 0,
          key: season.id || 0,
          name: season.title || season.i18n_title?.en || '',
          season_order: season.season_order || 0,
          disabled: false,
          status: season.status
            ? season.status === 'published'
              ? 'Enabled'
              : 'Disabled'
            : 'Disabled',
        })),
        count: seasons.length,
      };
    },
    createWithParentId: async (itemIds: number[]): Promise<void> => {
      if (!id || id === 'new') {
        message.error('Please save series first');
        return;
      }
      await seriesSeasonItemService.createItemsForParent(Number(id), itemIds);
      message.success('Successfully added to the series');
    },
    deleteWithParentId: async (itemIds: number[]): Promise<void> => {
      if (!id || id === 'new') {
        message.error('Please save series first');
        return;
      }
      await seriesSeasonItemService.deleteItemsForParent(Number(id), itemIds);
      message.success('Successfully removed from the series');
    },
  };

  return (
    <Component.BasePageComponent>
      <div style={{ padding: '24px' }}>
        <Form
          form={form}
          layout="vertical"
          className="series-edit2-form"
          onValuesChange={handleValuesChange}
        >
          {/* Series Metadata Section */}
          <Card title="Series Metadata" style={{ marginBottom: '24px' }}>
            {/* Content Type */}
            <Tooltip title="Content-Type 'tv-series' cannot be changed as this is series screen. *can be hidden.">
              <Form.Item label="Content Type" style={{ marginBottom: '16px' }}>
                <Button disabled>tv-series</Button>
              </Form.Item>
            </Tooltip>

            {/* Series Titles */}
            <div style={{ marginBottom: '16px' }}>
              <Form.Item label="Series Title" name="title" style={{ display: 'none' }} />
              <Tabs
                type="card"
                items={writeLanguages.map(lang => ({
                  key: lang.rfc_code.toLowerCase(),
                  label: `Series Title ${lang.rfc_code}`,
                  children: (
                    <Input
                      value={(i18nTitle as any)[lang.rfc_code.toLowerCase()] || ''}
                      onChange={e => {
                        const langCode = lang.rfc_code.toLowerCase();
                        const newI18nTitle = {
                          ...i18nTitle,
                          [langCode]: e.target.value,
                        } as I18nFieldData;
                        handleTitleChange(newI18nTitle);
                        // form 필드도 업데이트
                        if (langCode === 'en') {
                          form.setFieldValue('title', e.target.value);
                          form.setFieldValue('series_title_en', e.target.value);
                          markFieldChanged('title', 'title_en', 'series_title_en');
                        } else if (langCode === 'ko') {
                          form.setFieldValue('series_title_ko', e.target.value);
                          markFieldChanged('series_title_ko');
                        } else if (langCode === 'zh-hans') {
                          form.setFieldValue('series_title_zh_hans', e.target.value);
                          markFieldChanged('series_title_zh_hans');
                        } else if (langCode === 'vi') {
                          form.setFieldValue('series_title_vi', e.target.value);
                          markFieldChanged('series_title_vi');
                        }
                      }}
                      placeholder={lang.rfc_code.toLowerCase() === 'en' ? 'En required *' : ''}
                    />
                  ),
                }))}
              />
            </div>

            {/* Series Slug */}
            <Form.Item
              label="Series Slug (auto-gen from title en)*"
              name="slug"
              style={{ marginBottom: '16px' }}
              rules={[{ required: true, message: 'Please enter a slug' }]}
            >
              <Input />
            </Form.Item>
            {/* Age Rating and Country of Origin */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <Form.Item
                label="Age Rating (Default TV-G)"
                name="age_rating"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select defaultValue="TV-G">
                  {AGE_RATING.map(rating => (
                    <Select.Option key={rating} value={rating}>
                      {rating}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Country of Origin"
                name="country_of_origin"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select country"
                  onChange={handleCountryOfOriginChange}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={countries.map(country => ({
                    label: country.name,
                    value: country.id,
                  }))}
                />
              </Form.Item>
            </div>

            {/* cp from odk, cp from odc */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <Form.Item
                label="CP from ODK"
                name="cp_from_odk"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Search CP from ODK..."
                  filterOption={(input, option) =>
                    (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={cpFromOdkOptions}
                />
              </Form.Item>

              <Form.Item
                label="CP from ODC"
                name="cp_from_odc"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Search CP from ODC..."
                  filterOption={false}
                  onSearch={handleCpFromOdcSearch}
                  onClear={handleCpFromOdcClear}
                  loading={cpFromOdcLoading}
                  notFoundContent={cpFromOdcLoading ? 'Searching...' : 'No CP found'}
                  onDropdownVisibleChange={handleCpFromOdcDropdownVisibleChange}
                  options={cpFromOdcOptions}
                />
              </Form.Item>

              <Form.Item
                label="CP for third option"
                name="cp_from_odx"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Input />
              </Form.Item>
            </div>

            {/* Synopsis */}
            <div style={{ marginBottom: '16px' }}>
              <Tabs
                type="card"
                items={writeLanguages.map(lang => ({
                  key: lang.rfc_code.toLowerCase(),
                  label: `Synopsis ${lang.rfc_code}`,
                  children: (
                    <Input.TextArea
                      rows={6}
                      value={(i18nSynopsis as any)[lang.rfc_code.toLowerCase()] || ''}
                      onChange={e => {
                        setI18nSynopsis({
                          ...i18nSynopsis,
                          [lang.rfc_code.toLowerCase()]: e.target.value,
                        } as I18nFieldData);
                        markFieldChanged('i18n_synopsis');
                      }}
                      placeholder={lang.rfc_code.toLowerCase() === 'en' ? 'En required *' : ''}
                    />
                  ),
                }))}
              />
            </div>

            {/* Dates */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <Form.Item
                label="Series released date"
                name="release_date_start"
                style={{ flex: 1, marginBottom: 0 }}
                getValueFromEvent={(_date, dateString) => dateString || null}
                getValueProps={value => {
                  if (!value) return { value: null };
                  // value가 dayjs 객체인 경우 그대로 사용 (이미 PST로 변환된 상태)
                  if (dayjs.isDayjs(value)) {
                    return { value };
                  }
                  // 문자열인 경우 날짜로 파싱 (YYYY-MM-DD 형식)
                  if (typeof value === 'string') {
                    const parsed = dayjs(value, 'YYYY-MM-DD');
                    if (parsed.isValid()) {
                      return { value: parsed };
                    }
                  }
                  // 파싱 실패 시 null 반환
                  return { value: null };
                }}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD"
                  onChange={date => {
                    if (date) {
                      const year = date.year();
                      form.setFieldValue('produce_year', year);
                      markFieldChanged('produce_year');
                    }
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Series ended date"
                name="release_date_end"
                style={{ flex: 1, marginBottom: 0 }}
                getValueFromEvent={(_date, dateString) => dateString || null}
                getValueProps={value => {
                  if (!value) return { value: null };
                  // value가 dayjs 객체인 경우 그대로 사용 (이미 PST로 변환된 상태)
                  if (dayjs.isDayjs(value)) {
                    return { value };
                  }
                  // 문자열인 경우 날짜로 파싱 (YYYY-MM-DD 형식)
                  if (typeof value === 'string') {
                    const parsed = dayjs(value, 'YYYY-MM-DD');
                    if (parsed.isValid()) {
                      return { value: parsed };
                    }
                  }
                  // 파싱 실패 시 null 반환
                  return { value: null };
                }}
              >
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
              <Form.Item
                label="Production Year"
                name="produce_year"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Input type="number" min={0} />
              </Form.Item>
            </div>
          </Card>

          {/* Series Posters Section */}
          <Card title="Series Posters" style={{ marginBottom: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
              {POSTER_LANGUAGE_OPTIONS.map(lang => (
                <div key={lang.value} style={{ border: '1px solid #d9d9d9', padding: '16px' }}>
                  <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>{lang.label}</div>
                  {POSTER_ORIENTATIONS.map(orientation => {
                    const posters = groupedByLanguage[lang.value]?.[orientation.value] || [];
                    const poster = posters[0]; // Show first poster if exists
                    return (
                      <div key={orientation.value} style={{ marginBottom: '16px' }}>
                        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                          {orientation.label} ({orientation.dimensions})
                        </div>
                        {poster ? (
                          <div
                            style={{ position: 'relative' }}
                            onClick={() => window.open(poster.file, '_blank')}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                window.open(poster.file, '_blank');
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            <img
                              src={poster.file}
                              alt={orientation.label}
                              style={{ width: '100%', height: 'auto', cursor: 'pointer' }}
                            />
                            <Button
                              type="text"
                              danger
                              size="small"
                              style={{ position: 'absolute', top: 0, right: 0 }}
                              onClick={e => {
                                e.stopPropagation();
                                handlePosterRemove(poster);
                              }}
                            >
                              X
                            </Button>
                          </div>
                        ) : (
                          <Upload
                            accept="image/*"
                            showUploadList={false}
                            beforeUpload={() => false}
                            onChange={handleUploadWithLanguageAndOrientation(
                              lang.value,
                              orientation.value
                            )}
                          >
                            <div
                              style={{
                                width: '100%',
                                height: '120px',
                                border: '2px dashed #d9d9d9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                              }}
                            >
                              <span style={{ color: '#999' }}>Click to upload</span>
                            </div>
                          </Upload>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </Card>

          {/* Seasons Section */}
          <Card title="Seasons" style={{ marginBottom: '24px' }}>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
              <div>
                {id && (
                  <Button
                    type="primary"
                    onClick={() => {
                      if (!id || id === 'new') {
                        // 시리즈가 아직 저장되지 않은 경우, 현재 폼 데이터를 sessionStorage에 저장
                        const seriesFormData = {
                          slug: form.getFieldValue('slug'),
                          i18n_title: i18nTitle,
                          i18n_synopsis: i18nSynopsis,
                          i18n_seo_title: i18nSeoTitle,
                          i18n_seo_synopsis: i18nSeoSynopsis,
                          i18n_seo_keyword: i18nSeoKeyword,
                          age_rating: form.getFieldValue('age_rating'),
                          country_of_origin: form.getFieldValue('country_of_origin'),
                          produce_country: form.getFieldValue('produce_country'),
                          dubbing_mode: form.getFieldValue('dubbing_mode'),
                          production_year: form.getFieldValue('production_year'),
                          produce_year: form.getFieldValue('produce_year'),
                          release_date_start: form.getFieldValue('release_date_start'),
                          release_date_end: form.getFieldValue('release_date_end'),
                          release_weekday: form.getFieldValue('release_weekday'),
                          genres: form.getFieldValue('genres'),
                          tags: form.getFieldValue('tags'),
                          cp_from_odk: form.getFieldValue('cp_from_odk'),
                          cp_from_odc: form.getFieldValue('cp_from_odc'),
                          status: form.getFieldValue('status'),
                          enhanced_cast: enhancedCast,
                          service_auto_distribution_inherit: false, // 시즌은 기본적으로 자체 설정
                          service_auto_distributions: distributions,
                        };
                        sessionStorage.setItem('pendingSeriesData', JSON.stringify(seriesFormData));
                        window.open(`/season/edit2/new?series_id=pending`, '_blank');
                      } else {
                        // 시리즈가 이미 저장된 경우 기존대로 처리
                        window.open(`/season/edit2/new?series_id=${id}`, '_blank');
                      }
                    }}
                  >
                    Create Season
                  </Button>
                )}
              </div>
            </div>
            <Component.TransferComponent
              dataHandler={seasonDataHandler}
              columns={transferColumns}
            />
          </Card>

          {/* SEO Information Section */}
          <Card title="SEO Information" style={{ marginBottom: '24px' }}>
            {/* SEO Title */}
            <div style={{ marginBottom: '24px' }}>
              <Tabs
                type="card"
                items={writeLanguages.map(lang => ({
                  key: lang.rfc_code.toLowerCase(),
                  label: `SEO title ${lang.rfc_code}`,
                  children: (
                    <Input
                      value={(i18nSeoTitle as any)[lang.rfc_code.toLowerCase()] || ''}
                      onChange={e => {
                        setI18nSeoTitle({
                          ...i18nSeoTitle,
                          [lang.rfc_code.toLowerCase()]: e.target.value,
                        } as I18nFieldData);
                        markFieldChanged('i18n_seo_title');
                      }}
                      placeholder={lang.rfc_code.toLowerCase() === 'en' ? 'En required *' : ''}
                    />
                  ),
                }))}
              />
              <div style={{ marginTop: '8px' }}>
                <Button type="link" size="small" onClick={() => handleAutoFillSeo('title')}>
                  Auto fill with Series Titles
                </Button>
              </div>
            </div>

            <Divider />

            {/* SEO Keyword */}
            <div style={{ marginBottom: '24px' }}>
              <Tabs
                type="card"
                items={writeLanguages.map(lang => ({
                  key: lang.rfc_code.toLowerCase(),
                  label: `SEO keyword ${lang.rfc_code}`,
                  children: (
                    <Input
                      value={(i18nSeoKeyword as any)[lang.rfc_code.toLowerCase()] || ''}
                      onChange={e => {
                        setI18nSeoKeyword({
                          ...i18nSeoKeyword,
                          [lang.rfc_code.toLowerCase()]: e.target.value,
                        } as I18nFieldData);
                        markFieldChanged('i18n_seo_keyword');
                      }}
                      placeholder={lang.rfc_code.toLowerCase() === 'en' ? 'En required *' : ''}
                    />
                  ),
                }))}
              />
              <div style={{ marginTop: '8px' }}>
                <Button type="link" size="small" onClick={() => handleAutoFillSeo('keyword')}>
                  Auto fill with Series Titles
                </Button>
              </div>
            </div>

            <Divider />

            {/* SEO Synopsis */}
            <div style={{ marginBottom: '24px' }}>
              <Tabs
                type="card"
                items={writeLanguages.map(lang => ({
                  key: lang.rfc_code.toLowerCase(),
                  label: `SEO Synopsis ${lang.rfc_code}`,
                  children: (
                    <Input.TextArea
                      rows={6}
                      value={(i18nSeoSynopsis as any)[lang.rfc_code.toLowerCase()] || ''}
                      onChange={e => {
                        setI18nSeoSynopsis({
                          ...i18nSeoSynopsis,
                          [lang.rfc_code.toLowerCase()]: e.target.value,
                        } as I18nFieldData);
                        markFieldChanged('i18n_seo_synopsis');
                      }}
                      placeholder={lang.rfc_code.toLowerCase() === 'en' ? 'En required *' : ''}
                    />
                  ),
                }))}
              />
              <div style={{ marginTop: '8px' }}>
                <Button type="link" size="small" onClick={() => handleAutoFillSeo('synopsis')}>
                  Auto fill with Synopsis
                </Button>
              </div>
            </div>
          </Card>

          {/* Enhanced Cast Section */}
          <Card title="Enhanced Cast" style={{ marginBottom: '24px' }}>
            <EnhancedCastEditor
              value={enhancedCast}
              onChange={value => {
                setEnhancedCast(value);
                markFieldChanged('enhanced_cast');
              }}
              peopleList={searchedPeopleList}
              onSearchPeople={searchPeopleForEnhancedCast}
            />
          </Card>

          {/* Genres & Tags Section */}
          <Card title="Genres & Tags" style={{ marginBottom: '24px' }}>
            <Form.Item>
              <Form.Item name="categories" hidden />
              <Form.Item name="tags" hidden />
              <CustomSelector tabs={tabs} />
            </Form.Item>
          </Card>

          {/* 11DB Metadata Section */}
          <Card
            title={`11DB Metadata ${elevenDbSeriesId ? `{${elevenDbSeriesId}}` : ''}`}
            style={{ marginBottom: '24px' }}
          >
            <Form.Item label="11DB Series ID">
              <Input
                value={elevenDbSeriesId}
                onChange={e => setElevenDbSeriesId(e.target.value)}
                placeholder="Enter 11DB series ID"
              />
            </Form.Item>

            <Form.Item label="Awards">
              <Input
                style={{ width: '100%' }}
                placeholder="Enter award name"
                value={elevenDbAwards}
                onChange={e => {
                  setElevenDbAwards(e.target.value);
                }}
              />
            </Form.Item>

            <Form.Item label="Awards Category">
              <Input
                placeholder="Enter award category"
                value={elevenDbAwardsCategory}
                onChange={e => setElevenDbAwardsCategory(e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Related Series">
              <Input
                placeholder="Enter series ID or name"
                value={elevenDbRelatedSeries}
                onChange={e => setElevenDbRelatedSeries(e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Related Titles">
              <Input
                placeholder="Enter title ID or name"
                value={elevenDbRelatedTitles}
                onChange={e => setElevenDbRelatedTitles(e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Linked Articles">
              <Input
                placeholder="Enter article URL"
                value={elevenDbLinkedArticles}
                onChange={e => setElevenDbLinkedArticles(e.target.value)}
              />
            </Form.Item>
            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item label="User Ratings">
                <Input
                  placeholder="Enter user ratings"
                  value={elevenDbUserRatings}
                  onChange={e => setElevenDbUserRatings(e.target.value)}
                />
              </Form.Item>

              <Form.Item label="Critics Ratings">
                <Input
                  placeholder="Enter critics ratings"
                  value={elevenDbCriticsRatings}
                  onChange={e => setElevenDbCriticsRatings(e.target.value)}
                />
              </Form.Item>

              <Form.Item label="Movie Box Office">
                <Input
                  placeholder="Enter movie box office"
                  value={elevenDbMovieBoxOffice}
                  onChange={e => setElevenDbMovieBoxOffice(e.target.value)}
                />
              </Form.Item>
            </div>
          </Card>

          {/* Service Auto Distribution Section */}
          <Card title="Service Auto Distribution" style={{ marginBottom: '24px' }}>
            <p style={{ marginBottom: '16px', color: '#666' }}>
              Service Auto Distribution for the series.
            </p>

            <Table
              dataSource={(() => {
                const data: any[] = [];

                if (process.env.REACT_APP_ENV === 'production') {
                  // Production 서비스 추가
                  SERVICE_OPTIONS.forEach(service => {
                    const dist = distributions.find(
                      d => d.service_name === service.name && d.environment === 'production'
                    );
                    data.push({
                      key: `${service.name}-production`,
                      service: service.name,
                      environment: 'production',
                      dist: dist || {
                        service_name: service.name,
                        environment: 'production',
                        enabled: false,
                        auto_publish: false,
                        subtitle_langs: [],
                      },
                    });
                  });
                } else {
                  // Staging 서비스 추가
                  SERVICE_OPTIONS.forEach(service => {
                    const dist = distributions.find(
                      d => d.service_name === service.name && d.environment === 'staging'
                    );
                    data.push({
                      key: `${service.name}-staging`,
                      service: service.name,
                      environment: 'staging',
                      dist: dist || {
                        service_name: service.name,
                        environment: 'staging',
                        enabled: false,
                        auto_publish: false,
                        subtitle_langs: [],
                      },
                    });
                  });
                }

                // Divider 추가
                // data.push({
                //   key: 'divider',
                //   isDivider: true,
                // });

                return data;
              })()}
              columns={[
                {
                  title: 'Service',
                  dataIndex: 'service',
                  key: 'service',
                  width: '320px',
                  render: (text: string, record: any) => {
                    if (record.isDivider) {
                      return {
                        children: <div style={{ height: '1px', background: '#e8e8e8' }} />,
                        props: { colSpan: 3 },
                      };
                    }
                    return (
                      <Checkbox
                        checked={record.dist.enabled || false}
                        onChange={e => {
                          updateDistribution(record.service, record.environment, {
                            enabled: e.target.checked,
                          });
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>
                          {text} ({record.environment === 'production' ? 'Production' : 'Staging'})
                        </span>
                      </Checkbox>
                    );
                  },
                },
                {
                  title: 'Auto Publish',
                  key: 'auto_publish',
                  width: '150px',
                  render: (_: any, record: any) => {
                    if (record.isDivider) {
                      return { props: { colSpan: 0 } };
                    }
                    return (
                      <Checkbox
                        checked={record.dist.auto_publish || false}
                        disabled={!record.dist.enabled}
                        onChange={e =>
                          updateDistribution(record.service, record.environment, {
                            auto_publish: e.target.checked,
                          })
                        }
                      >
                        Auto Publish
                      </Checkbox>
                    );
                  },
                },
                {
                  title: 'Subtitles',
                  key: 'subtitles',
                  render: (_: any, record: any) => {
                    if (record.isDivider) {
                      return { props: { colSpan: 0 } };
                    }
                    return (
                      <Space wrap>
                        {SUBTITLE_LANGUAGES.map(lang => (
                          <Checkbox
                            key={lang.value}
                            checked={record.dist.subtitle_langs?.includes(lang.value) || false}
                            disabled={!record.dist.enabled}
                            onChange={e => {
                              const newSubtitles = e.target.checked
                                ? [...(record.dist.subtitle_langs || []), lang.value]
                                : (record.dist.subtitle_langs || []).filter(
                                    (s: string) => s !== lang.value
                                  );
                              updateDistribution(record.service, record.environment, {
                                subtitle_langs: newSubtitles,
                              });
                            }}
                          >
                            {lang.label}
                          </Checkbox>
                        ))}
                      </Space>
                    );
                  },
                },
              ]}
              pagination={false}
              bordered
            />
          </Card>

          {/* Save Buttons */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'right',
              alignItems: 'center',
              gap: '16px',
              marginTop: '32px',
            }}
          >
            {/* <Card>
              <Form.Item label="Series Status" name="status" style={{ marginBottom: 0 }}>
                <Select
                  style={{ width: '200px' }}
                  value={
                    form.getFieldValue('status') === true ||
                    form.getFieldValue('status') === 'published'
                      ? 'published'
                      : 'unpublished'
                  }
                  onChange={value => {
                    form.setFieldValue('status', value);
                  }}
                >
                  <Select.Option value="published">Enabled</Select.Option>
                  <Select.Option value="unpublished">Disabled</Select.Option>
                </Select>
              </Form.Item>
            </Card> */}
            {id && id !== 'new' ? (
              <>
                <div style={{ padding: '8px', backgroundColor: '#fffbe6', borderRadius: '4px' }}>
                  <div style={{ fontSize: '12px' }}>
                    Save to Series Only - save changes, metadata to only series.
                  </div>
                  <div style={{ fontSize: '12px' }}>
                    Save Series / Save to Season 1 - save changes to series and copy metadata to
                    season 1. These two buttons show only if editing an existing series.
                  </div>
                </div>
                <Button type="primary" size="large" onClick={() => handleSave(false)}>
                  Save to Series Only
                </Button>
                <Button type="primary" size="large" onClick={() => handleSave(true)}>
                  Save Series / Save to Season 1
                </Button>
              </>
            ) : (
              <>
                <div style={{ padding: '8px', backgroundColor: '#fffbe6', borderRadius: '4px' }}>
                  <div style={{ fontSize: '12px' }}>
                    Create Series & Create Season 1 - save changes to series and create/copy
                    metadata to season 1. Show if creating a series.
                  </div>
                </div>
                <Button type="primary" size="large" onClick={() => handleSave(true)}>
                  Create Series & Create Season 1
                </Button>
              </>
            )}
          </div>
        </Form>
      </div>
    </Component.BasePageComponent>
  );
};

export default SeriesEdit2;
